import Axios, { AxiosRequestConfig } from 'axios';
import { getLocale } from '@app/i18n';
import { createApiException } from './exceptions';

export let API_URL = process.env.NX_API_URL;

if (
  window.location.hostname.endsWith('tandemz.io') &&
  API_URL?.endsWith('bilendi-ux.com')
) {
  API_URL = 'https://api.tandemz.io';
}

if (window.location.hostname.endsWith('bilendi-ux.ch')) {
  API_URL = 'https://api.bilendi-ux.ch';
}

export const fetchWithAuth = async <T = any>(
  options: AxiosRequestConfig,
): Promise<T> => {
  try {
    const result = await Axios({
      ...options,
      withCredentials: true,
      adapter: 'fetch',
      fetchOptions: {
        cache: 'default',
      },
      headers: {
        'Accept-Language': getLocale(),
        ...options.headers,
      },
    });
    return result.data;
  } catch (e) {
    throw createApiException(e as any);
  }
};

export const queryFetch = <T = any>(
  options: AxiosRequestConfig,
): Promise<T> & { cancel: () => void } => {
  const source = Axios.CancelToken.source();

  const promise = fetchWithAuth({
    cancelToken: source.token,
    ...options,
  });

  // @ts-expect-error cancel is added by axios but not in Promise definition
  promise.cancel = () => {
    source.cancel('cancelled');
  };

  // @ts-expect-error cancel is added by axios but not in Promise definition
  return promise;
};

export const queryGet =
  <T = any>(path: string, params?: object) =>
  () => {
    return queryFetch<T>({
      url: `${API_URL}${path}`,
      params: params,
      method: 'GET',
    });
  };

export interface QueryUpdateOptions {
  params?: object;
}

export const queryUpdate =
  (path: string | ((data: any) => string), options?: QueryUpdateOptions) =>
  (data: any) => {
    const finalPath = typeof path === 'string' ? path : path(data);

    return queryFetch({
      url: `${API_URL}${finalPath}`,
      params: options && options.params,
      method: 'PATCH',
      data,
    });
  };

export const queryReplace =
  (path: string | ((data: any) => string), options?: QueryUpdateOptions) =>
  (data: any) => {
    const finalPath = typeof path === 'string' ? path : path(data);

    return queryFetch({
      url: `${API_URL}${finalPath}`,
      params: options && options.params,
      method: 'PUT',
      data,
    });
  };

export type QueryCreateOptions = QueryUpdateOptions;

export const queryCreate =
  (path: string, options?: QueryCreateOptions) => (data?: any) =>
    queryFetch({
      url: `${API_URL}${path}`,
      params: options && options.params,
      method: 'POST',
      data,
    });

export const queryDelete =
  (path: string | ((data: any) => string), options?: QueryUpdateOptions) =>
  (data?: any) => {
    const finalPath = typeof path === 'string' ? path : path(data);

    return queryFetch({
      url: `${API_URL}${finalPath}`,
      params: options && options.params,
      method: 'DELETE',
      data,
    });
  };
